import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { fmtEUR, withVAT, finalPrice } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useWishlist } from "@/lib/wishlist";
import { toast } from "sonner";

export default function ProductCard({ product }) {
    const { add } = useCart();
    const { user } = useAuth();
    const { has, toggle } = useWishlist();
    const wished = has ? has(product.id) : false;
    const inStock = (product.stock ?? 0) > 0;
    const vat = Number(product.vat_rate ?? 23);
    const base = Number(product.price ?? 0);
    const finalP = finalPrice(product);
    const hasCampaign = product.campaign_id && finalP < base;
    const discount = hasCampaign && base > 0 ? Math.round(((base - finalP) / base) * 100) : null;

    return (
        <article
            className="card-edge card-edge-hover-magenta flex flex-col"
            data-testid={`product-card-${product.sku}`}
        >
            <Link to={`/produto/${product.id}`} className="block relative bg-muted aspect-square overflow-hidden">
                {product.image_url ? (
                    <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center font-mono text-xs text-ink/40">
                        sem imagem
                    </div>
                )}
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                    {product.is_original && (
                        <span className="badge-edge bg-ink text-white border-ink">Original</span>
                    )}
                    {!product.is_original && !product.is_refurbished && product.category_slug?.includes("toner") && (
                        <span className="badge-edge bg-paper text-ink border-ink">Compatível</span>
                    )}
                    {product.is_refurbished && (
                        <span className="badge-edge bg-accent-green text-white border-accent-green">
                            Recondicionado
                        </span>
                    )}
                    {discount && (
                        <span
                            className="badge-edge bg-magenta text-white border-magenta"
                            data-testid={`campaign-badge-${product.sku}`}
                            title={product.campaign_name || "Campanha activa"}
                        >
                            -{discount}%
                        </span>
                    )}
                </div>
                {/* Wishlist heart (only for logged customers) */}
                {user && user.role === "customer" && (
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggle(product.id).then((added) => {
                                toast.success(added ? "Adicionado à wishlist" : "Removido da wishlist", {
                                    description: product.name,
                                });
                            });
                        }}
                        className={`absolute top-3 right-3 p-2 border-2 transition-all ${
                            wished
                                ? "border-magenta bg-magenta text-white"
                                : "border-ink bg-white text-ink hover:border-magenta hover:text-magenta"
                        }`}
                        title={wished ? "Remover da wishlist" : "Adicionar à wishlist"}
                        data-testid={`wishlist-btn-${product.sku}`}
                    >
                        <Heart size={14} fill={wished ? "currentColor" : "none"} />
                    </button>
                )}
            </Link>

            <div className="p-4 flex flex-col gap-2 flex-1 border-t-2 border-ink">
                <div className="flex items-center justify-between">
                    <span className="label-mono">{product.brand || "—"}</span>
                    <span className="text-[11px] font-mono text-ink/60">{product.sku}</span>
                </div>
                <Link to={`/produto/${product.id}`}>
                    <h3 className="font-display font-bold text-lg leading-tight line-clamp-2 hover:text-magenta">
                        {product.name}
                    </h3>
                </Link>
                {(product.color || product.yield_value) && (
                    <div className="flex items-center gap-2 text-[11px] font-mono text-ink/60">
                        {product.color && <span className="capitalize">{product.color}</span>}
                        {product.color && product.yield_value && <span>·</span>}
                        {product.yield_value && (
                            <span>
                                {product.yield_value}{" "}
                                {product.yield_unit === "ml" ? "ml" : "cópias"}
                            </span>
                        )}
                    </div>
                )}
                <div className="mt-auto pt-3 flex items-end justify-between gap-2">
                    <div>
                        {hasCampaign && (
                            <div className="text-xs font-mono text-ink/50 line-through" data-testid="price-strike">
                                {fmtEUR(base)} s/IVA
                            </div>
                        )}
                        <div className="font-display font-black text-2xl tracking-tight text-magenta" data-testid="product-price">
                            {fmtEUR(finalP)}
                            <span className="ml-2 text-[10px] font-mono align-top text-ink/60">s/IVA</span>
                        </div>
                        <div className="text-[11px] font-mono text-ink/70 mt-0.5">
                            {fmtEUR(withVAT(finalP, vat))} c/IVA · IVA {vat}%
                        </div>
                    </div>
                    <span
                        className={`text-[11px] font-mono uppercase tracking-wider ${
                            inStock ? "text-accent-green" : "text-destructive"
                        }`}
                    >
                        {inStock ? `${product.stock} em stock` : "Esgotado"}
                    </span>
                </div>
                <button
                    disabled={!inStock}
                    onClick={() => {
                        add(product, 1);
                        toast.success("Adicionado ao carrinho", { description: product.name });
                    }}
                    className="mt-2 btn-primary disabled:bg-muted disabled:text-ink/40 disabled:border-ink/30 disabled:cursor-not-allowed"
                    data-testid={`add-to-cart-${product.sku}`}
                >
                    {inStock ? "Adicionar" : "Indisponível"}
                </button>
            </div>
        </article>
    );
}
